import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import type { AnalyticsIdentity } from "@patent-studio/api-types";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { useAuth } from "@/contexts/AuthContext";
import { get } from "@/services/api-client";

/**
 * Attributes what happens in this browser to whoever is signed in, under the
 * identity the API serves: a pilot registrant's registrant ID, the one the
 * server's funnel events carry, so both land on one PostHog person; staff
 * keep their subject, marked internal. The email is never sent.
 *
 * Until the API answers, nobody is identified. Identifying by the subject in
 * the meantime would make the very second person this exists to prevent.
 *
 * The SDK keeps the last identity in browser storage, so clearing it is not
 * only a sign-out concern. A restoration finishing with nobody signed in has
 * to clear it too, or the next person inherits it.
 */
export function useAnalyticsIdentity() {
  const analytics = useAnalytics();
  const { user, isLoading } = useAuth();
  const subject = isLoading ? undefined : user?.userId;
  const identity = useQuery({
    queryKey: ["analytics-identity", subject],
    queryFn: () => get<AnalyticsIdentity>("/analytics/identity"),
    enabled: subject !== undefined,
    // Who someone is does not change while they stay signed in.
    staleTime: Infinity,
  });
  // undefined: nothing said yet. null: signed out, or between two people.
  // A string: the subject whose identity the SDK holds.
  const heldForRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    // While the session is being restored, either answer would be a guess.
    if (isLoading) return;

    if (!user) {
      if (heldForRef.current === null) return;
      heldForRef.current = null;
      analytics.reset();
      return;
    }

    // Identifying over the top of someone else links the two accounts as one
    // person, and the public login page means a switch needs no signed-out
    // state in between, so the old identity goes before the new one arrives.
    if (heldForRef.current && heldForRef.current !== user.userId) {
      heldForRef.current = null;
      analytics.reset();
    }

    const served = identity.data;
    if (!served || heldForRef.current === user.userId) return;
    heldForRef.current = user.userId;
    analytics.identify(served.distinct_id, { is_internal: served.is_internal });
    analytics.register({ is_internal: served.is_internal });
  }, [analytics, isLoading, user, identity.data]);
}
